import React, { useState, useEffect } from 'react';

const Plans = () => {
    const [additionalProfessionals, setAdditionalProfessionals] = useState(0);
    const [totalPrice, setTotalPrice] = useState(650);
    const [pricingPeriod, setPricingPeriod] = useState('monthly'); // 'monthly' or 'annual'

    // Base prices constants
    const normalBasePrice = 350;
    const normalOriginalPrice = 450;
    const premiumBasePrice = 650;
    const premiumOriginalPrice = 850;
    const additionalProPrice = 150;

    // Calculate the pricing based on professionals and pricing period
    useEffect(() => {
        let calculatedPrice = premiumBasePrice + (additionalProfessionals * additionalProPrice);

        if (pricingPeriod === 'annual') {
            // For annual, calculate 8 months instead of 12 (4 months free)
            calculatedPrice = calculatedPrice * 8;
        }

        setTotalPrice(calculatedPrice);
    }, [additionalProfessionals, pricingPeriod, premiumBasePrice]);

    // Normal plan features
    const normalPlanFeatures = [
        "Página web profesional personalizada",
        "Formulario para clientes",
        "Catálogo de servicios con precios",
        "Optimización para dispositivos móviles",
        "Visibilidad en Google"
    ];

    // Premium plan additional features
    const premiumPlanFeatures = [
        "Sistema de reservas online 24/7",
        "Calendario de servicios por profesional",
        "Recordatorios automáticos por WhatsApp y Email",
        "Estadísticas básicas de uso",
        "Citas ilimitadas",
    ];

    return (
        <section className="py-32 bg-white relative overflow-hidden" id="planes">
            {/* Subtle background elements */}
            <div className="absolute top-40 right-0 w-72 h-72 rounded-full bg-primary/5 blur-3xl"></div>
            <div className="absolute bottom-40 left-0 w-80 h-80 rounded-full bg-secondary/5 blur-3xl"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-5xl font-bold mb-6 text-secondary">
                        Planes para tu Negocio
                    </h2>
                    <p className="text-xl text-gray-600 mb-8">
                        Soluciones flexibles para emprendedores y negocios de todos los tamaños
                    </p>

                    {/* Pricing period selector - simplified and sleeker */}
                    <div className="inline-flex items-center bg-gray-100 p-1 rounded-xl mx-auto">
                        <button
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${pricingPeriod === 'monthly'
                                ? 'bg-white text-secondary shadow-md'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => setPricingPeriod('monthly')}
                        >
                            Mensual
                        </button>
                        <button
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${pricingPeriod === 'annual'
                                ? 'bg-white text-secondary shadow-md'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => setPricingPeriod('annual')}
                        >
                            Anual <span className="text-primary text-xs font-bold ml-1">4 meses gratis</span>
                        </button>
                    </div>
                </div>

                {/* Plans container */}
                <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                    {/* Normal plan - left column */}
                    <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                        <div className="p-8 flex flex-col h-full">
                            <div className="flex-grow">
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-1">Plan Básico</h3>
                                        <p className="text-gray-600 text-sm">Presencia web profesional</p>
                                    </div>
                                    <div className="bg-gray-100 rounded-lg p-2">
                                        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
                                        </svg>
                                    </div>
                                </div>

                                <div className="mb-6 border-b border-gray-100 pb-6">
                                    <div className="flex items-baseline">
                                        <span className="text-gray-400 line-through mr-2 text-md">
                                            {pricingPeriod === 'annual' ? `$${normalOriginalPrice * 12}` : `$${normalOriginalPrice}`}
                                        </span>
                                        <span className="text-4xl font-bold text-secondary">
                                            {pricingPeriod === 'annual' ? `$${normalBasePrice * 8}` : `$${normalBasePrice}`}
                                        </span>
                                        <span className="text-gray-500 ml-1 text-sm">UYU</span>
                                        {pricingPeriod === 'annual' && (
                                            <span className="ml-2 text-xs text-gray-500">/año</span>
                                        )}
                                    </div>
                                    {pricingPeriod === 'annual' && (
                                        <p className="text-primary text-sm font-medium mt-1">
                                            4 meses gratis al pagar anualmente
                                        </p>
                                    )}
                                </div>

                                <p className="text-gray-700 mb-6 font-medium">
                                    Ideal para profesionales que quieren tener presencia online pero gestionan sus reservas por teléfono o WhatsApp.
                                </p>

                                <h4 className="text-sm font-bold uppercase text-gray-500 tracking-wider mb-3">Incluye:</h4>
                                <ul className="space-y-3 mb-8">
                                    {normalPlanFeatures.map((feature, index) => (
                                        <li key={index} className="flex items-start">
                                            <svg className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                                            </svg>
                                            <span className="text-gray-700">{feature}</span>
                                        </li>
                                    ))}
                                    <li className="flex items-start opacity-50">
                                        <svg className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"></path>
                                        </svg>
                                        <span className="text-gray-500">Sin sistema de agenda</span>
                                    </li>
                                </ul>
                            </div>

                            <button className="w-full py-3.5 px-4 rounded-lg bg-primary hover:bg-primary-dark text-white font-medium transition-all duration-300 shadow-md text-sm"
                                onClick={() => {
                                    window.open('https://wa.me/59892748021?text=Hola!%20Me%20interesa%20el%20Plan%20Básico%20de%20Grovi%20para%20mi%20negocio', '_blank');
                                }}
                            >
                                Comenzar
                            </button>
                        </div>
                    </div>

                    {/* Premium plan - right column */}
                    <div className="bg-white rounded-2xl overflow-hidden shadow-xl border-2 border-secondary transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl relative">
                        {/* Popular badge */}
                        <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-4 py-1 uppercase tracking-wider shadow-md rounded-bl-lg">
                            Popular
                        </div>

                        <div className="p-8 flex flex-col h-full">
                            <div className="flex-grow">
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <h3 className="text-2xl font-bold text-secondary mb-1">Plan Premium</h3>
                                        <p className="text-gray-600 text-sm">Web + Sistema de agenda</p>
                                    </div>
                                    <div className="bg-secondary text-white rounded-lg p-2">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                        </svg>
                                    </div>
                                </div>

                                <div className="mb-6 border-b border-gray-100 pb-6">
                                    <div className="flex items-baseline">
                                        <span className="text-gray-400 line-through mr-2 text-md">
                                            {pricingPeriod === 'annual' ? `$${premiumOriginalPrice * 12}` : `$${premiumOriginalPrice}`}
                                        </span>
                                        <span className="text-4xl font-bold text-secondary">
                                            ${totalPrice}
                                        </span>
                                        <span className="text-gray-500 ml-1 text-sm">UYU</span>
                                        {pricingPeriod === 'annual' && (
                                            <span className="ml-2 text-xs text-gray-500">/año</span>
                                        )}
                                    </div>
                                    {pricingPeriod === 'annual' && (
                                        <p className="text-primary text-sm font-medium mt-1">
                                            4 meses gratis al pagar anualmente
                                        </p>
                                    )}
                                </div>

                                {/* Professional selector - simplified */}
                                <div className="mb-6 bg-gray-50 p-4 rounded-xl">
                                    <div className="flex justify-between items-center text-sm text-gray-700 mb-2">
                                        <span>Cantidad de profesionales:</span>
                                        <span className="font-bold text-secondary">{additionalProfessionals + 1}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="10"
                                        value={additionalProfessionals}
                                        onChange={(e) => setAdditionalProfessionals(parseInt(e.target.value))}
                                        className="w-full accent-primary"
                                        aria-label="Seleccionar número de profesionales"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>1</span>
                                        <span>5</span>
                                        <span>11</span>
                                    </div>
                                    <p className="text-gray-500 text-xs mt-3">
                                        1 profesional incluido. Cada profesional adicional: +{pricingPeriod === 'annual' ? `${additionalProPrice * 8}` : additionalProPrice} UYU
                                    </p>
                                </div>

                                <div>
                                    <h4 className="text-sm font-bold uppercase text-gray-500 tracking-wider mb-3">Todo lo del plan básico, y además:</h4>
                                    <ul className="space-y-3 mb-8">
                                        {premiumPlanFeatures.map((feature, index) => (
                                            <li key={index} className="flex items-start">
                                                <svg className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"></path>
                                                </svg>
                                                <span className="text-gray-700">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            {/* Special offer banner */}
                            <div className="mb-4 bg-gray-50 rounded-xl p-5 shadow-sm">
                                <div className="flex flex-col md:flex-row items-center justify-center text-center md:text-left">
                                    <div className="bg-white rounded-full p-3 md:mr-6 mb-4 md:mb-0 flex-shrink-0">
                                        <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H10a3 3 0 013 3v1a1 1 0 102 0v-1a5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-1">Ofertas Especiales</h3>
                                        <div className="flex flex-wrap justify-center md:justify-start gap-4">
                                            <span className="inline-flex items-center text-sm text-gray-700">
                                                <span className="mr-2 text-primary">✓</span>
                                                Recordatorios por WhatsApp gratis (primer mes)
                                            </span>
                                            <span className="inline-flex items-center text-sm text-gray-500">
                                                <span className="mr-2 text-secondary">ℹ</span>
                                                Normalmente $150 cada 100 mensajes
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button className="w-full py-3.5 px-4 rounded-lg bg-secondary hover:bg-secondary-dark text-white font-medium transition-all duration-300 shadow-md text-sm"
                                onClick={() => {
                                    window.open('https://wa.me/59892748021?text=Hola!%20Me%20interesa%20el%20Plan%20Premium%20de%20Grovi%20para%20mi%20negocio', '_blank');
                                }}
                            >
                                Comenzar
                            </button>
                        </div>
                    </div>
                </div>

                {/* Additional info section */}
                <div className="text-center mt-12">
                    <p className="text-gray-600 text-sm">¿Necesitas un plan personalizado con más profesionales?</p>
                    <a href="https://wa.me/59892748021?text=Hola!%20Necesito%20información%20sobre%20un%20plan%20personalizado%20para%20mi%20negocio" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline inline-block mt-1 text-sm">
                        Contacta con nuestro equipo
                    </a>
                </div>
            </div>
        </section>
    );
};

export default Plans; 
